import { useEffect, useState, useRef } from "react";
import {
  Scale, Upload, Image as ImageIcon, Mic, FileText, Trash2, Loader2,
  Gavel, Volume2, Plus, ArrowLeft
} from "lucide-react";
import Sidebar from "../components/Sidebar";
import VoiceMicButton from "../components/VoiceMicButton";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import api, { API } from "../lib/api";
import { toast } from "sonner";

export default function Analyses() {
  const [analyses, setAnalyses] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [mode, setMode] = useState("list"); // list | new | view
  const [analyzing, setAnalyzing] = useState(false);

  // Form state
  const [tab, setTab] = useState("text");
  const [verdictMode, setVerdictMode] = useState("nuanced");
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  const [context, setContext] = useState("");
  const [file, setFile] = useState(null);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const [a, c] = await Promise.all([
        api.get("/analyses"),
        api.get("/chat/conversations"),
      ]);
      setAnalyses(a.data);
      setConversations(c.data);
    } catch (e) {
      console.warn("Load error", e);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const reset = () => {
    setTitle(""); setText(""); setContext(""); setFile(null);
    setVerdictMode("nuanced"); setTab("text");
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setAnalyzing(true);
    try {
      let res;
      if (tab === "text") {
        if (!text.trim()) { toast.error("Décrivez la situation"); setAnalyzing(false); return; }
        res = await api.post("/analysis/text", {
          text, mode: verdictMode, title: title || null, context: context || null,
        });
      } else {
        if (!file) { toast.error("Sélectionnez un fichier"); setAnalyzing(false); return; }
        const fd = new FormData();
        fd.append("file", file);
        fd.append("mode", verdictMode);
        if (title) fd.append("title", title);
        if (context) fd.append("context", context);
        const endpoint = tab === "image" ? "/analysis/image" : "/analysis/audio";
        res = await api.post(endpoint, fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 180000,
        });
      }
      toast.success("Analyse réalisée");
      setSelected(res.data);
      setMode("view");
      reset();
      load();
    } catch (err) {
      const msg = err.response?.data?.detail || "Erreur d'analyse";
      toast.error(msg);
    } finally {
      setAnalyzing(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/analyses/${id}`);
      toast.success("Analyse supprimée");
      if (selected?.analysis_id === id) { setSelected(null); setMode("list"); }
      load();
    } catch {
      toast.error("Suppression échouée");
    }
  };

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar conversations={conversations} onNewChat={() => (window.location.href = "/dashboard")} />
      <main className="flex-1 flex flex-col min-w-0" data-testid="analyses-main">
        <header className="border-b border-slate-200 bg-white/80 backdrop-blur-xl sticky top-0 z-10">
          <div className="px-6 py-4 flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Analyse</p>
              <h1 className="font-serif-display text-xl font-medium tracking-tight text-slate-900">
                Qui a raison ?
              </h1>
            </div>
            {mode === "list" && (
              <Button
                onClick={() => { setMode("new"); setSelected(null); reset(); }}
                className="rounded-sm bg-slate-900 hover:bg-slate-800"
                data-testid="new-analysis-button"
              >
                <Plus className="h-4 w-4 mr-2" /> Nouvelle analyse
              </Button>
            )}
            {mode !== "list" && (
              <Button
                onClick={() => { setMode("list"); setSelected(null); }}
                variant="ghost"
                className="rounded-sm"
                data-testid="back-to-analyses-list"
              >
                <ArrowLeft className="h-4 w-4 mr-2" /> Retour
              </Button>
            )}
          </div>
        </header>

        <div className="flex-1 overflow-y-auto custom-scroll p-6">
          <div className="max-w-5xl mx-auto">
            {mode === "list" && (
              <ListView analyses={analyses} onOpen={(a) => { setSelected(a); setMode("view"); }} onDelete={handleDelete} onCreate={() => setMode("new")} />
            )}
            {mode === "new" && (
              <NewAnalysisForm
                tab={tab} setTab={setTab}
                verdictMode={verdictMode} setVerdictMode={setVerdictMode}
                title={title} setTitle={setTitle}
                text={text} setText={setText}
                context={context} setContext={setContext}
                file={file} setFile={setFile}
                fileRef={fileRef}
                onSubmit={handleSubmit}
                analyzing={analyzing}
              />
            )}
            {mode === "view" && selected && (
              <AnalysisView analysis={selected} onDelete={handleDelete} />
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

function ListView({ analyses, onOpen, onDelete, onCreate }) {
  if (analyses.length === 0) {
    return (
      <div className="text-center py-20" data-testid="analyses-empty">
        <Gavel className="h-10 w-10 mx-auto text-slate-300 mb-4" />
        <h3 className="font-serif-display text-2xl font-medium mb-2">Aucune analyse</h3>
        <p className="text-sm text-slate-500 mb-6 max-w-md mx-auto">
          Soumettez une situation, une photo (contrat, SMS, courrier) ou un enregistrement audio.
          L'IA détermine qui a raison selon le droit belge.
        </p>
        <Button onClick={onCreate} className="rounded-sm bg-slate-900 hover:bg-slate-800" data-testid="empty-new-analysis-button">
          <Plus className="h-4 w-4 mr-2" /> Démarrer une analyse
        </Button>
      </div>
    );
  }
  const ICONS = { text: FileText, image: ImageIcon, audio: Volume2 };
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {analyses.map((a) => {
        const Icon = ICONS[a.source_type] || FileText;
        return (
          <div
            key={a.analysis_id}
            className="group bg-white border border-slate-200 rounded-sm p-6 hover:border-slate-900 hover:shadow-md transition-all cursor-pointer"
            onClick={() => onOpen(a)}
            data-testid={`analysis-card-${a.analysis_id}`}
          >
            <div className="flex items-start justify-between mb-4">
              <Icon className="h-4 w-4 text-[#B89C65]" />
              <span className="text-xs uppercase tracking-widest text-slate-400">{a.mode === "verdict" ? "Verdict" : "Nuancée"}</span>
            </div>
            <p className="text-xs uppercase tracking-widest text-slate-400 mb-2">{a.source_type === "text" ? "Texte" : a.source_type === "image" ? "Image" : "Audio"}</p>
            <p className="text-sm font-medium text-slate-900 line-clamp-2 mb-4">{a.title}</p>
            <div className="flex items-center justify-between text-xs text-slate-500">
              <span>{new Date(a.created_at).toLocaleDateString("fr-BE")}</span>
              <button
                onClick={(e) => { e.stopPropagation(); onDelete(a.analysis_id); }}
                className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-400 hover:text-red-500"
                data-testid={`delete-analysis-${a.analysis_id}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function NewAnalysisForm({ tab, setTab, verdictMode, setVerdictMode, title, setTitle, text, setText, context, setContext, file, setFile, fileRef, onSubmit, analyzing }) {
  return (
    <form onSubmit={onSubmit} className="bg-white border border-slate-200 rounded-sm p-8" data-testid="new-analysis-form">
      <p className="text-xs uppercase tracking-[0.2em] text-[#B89C65] mb-2">Nouvelle analyse</p>
      <h2 className="font-serif-display text-3xl font-medium mb-2">Qui a raison ? L'IA tranche.</h2>
      <p className="text-sm text-slate-500 mb-8">
        Soumettez du texte, une image (contrat, SMS, email) ou un enregistrement audio.
        L'IA analyse selon le droit belge.
      </p>

      <Tabs value={tab} onValueChange={setTab} className="mb-6">
        <TabsList className="rounded-sm grid grid-cols-3 w-full max-w-md">
          <TabsTrigger value="text" className="rounded-sm" data-testid="tab-text">
            <FileText className="h-3.5 w-3.5 mr-2" /> Texte
          </TabsTrigger>
          <TabsTrigger value="image" className="rounded-sm" data-testid="tab-image">
            <ImageIcon className="h-3.5 w-3.5 mr-2" /> Photo
          </TabsTrigger>
          <TabsTrigger value="audio" className="rounded-sm" data-testid="tab-audio">
            <Mic className="h-3.5 w-3.5 mr-2" /> Audio
          </TabsTrigger>
        </TabsList>

        <TabsContent value="text" className="mt-6">
          <Label className="text-xs uppercase tracking-widest mb-2 block">Décrivez la situation</Label>
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={8}
            className="rounded-sm"
            placeholder="Exemple : Mon propriétaire refuse de me rendre ma garantie locative 4 mois après mon départ alors que l'état des lieux était nickel..."
            data-testid="analysis-text-input"
          />
        </TabsContent>

        <TabsContent value="image" className="mt-6">
          <Label className="text-xs uppercase tracking-widest mb-2 block">Photo (contrat, SMS, email, facture, amende)</Label>
          <div
            className="border border-dashed border-slate-300 rounded-sm p-8 text-center cursor-pointer hover:border-slate-900 hover:bg-slate-50 transition-all"
            onClick={() => fileRef.current?.click()}
            data-testid="image-dropzone"
          >
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="image-file-input"
            />
            {file ? (
              <div>
                <ImageIcon className="h-8 w-8 mx-auto text-[#B89C65] mb-3" />
                <p className="text-sm font-medium text-slate-900">{file.name}</p>
                <p className="text-xs text-slate-500 mt-1">{(file.size / 1024).toFixed(1)} KB · cliquer pour changer</p>
              </div>
            ) : (
              <div>
                <Upload className="h-8 w-8 mx-auto text-slate-400 mb-3" />
                <p className="text-sm text-slate-700 font-medium">Cliquer pour téléverser</p>
                <p className="text-xs text-slate-500 mt-1">JPEG, PNG ou WEBP · max 15MB</p>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="audio" className="mt-6">
          <Label className="text-xs uppercase tracking-widest mb-2 block">Enregistrement audio</Label>
          <div
            className="border border-dashed border-slate-300 rounded-sm p-8 text-center cursor-pointer hover:border-slate-900 hover:bg-slate-50 transition-all"
            onClick={() => fileRef.current?.click()}
            data-testid="audio-dropzone"
          >
            <input
              ref={fileRef}
              type="file"
              accept="audio/*,.mp3,.wav,.m4a,.webm,.mp4"
              className="hidden"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="audio-file-input"
            />
            {file ? (
              <div>
                <Volume2 className="h-8 w-8 mx-auto text-[#B89C65] mb-3" />
                <p className="text-sm font-medium text-slate-900">{file.name}</p>
                <p className="text-xs text-slate-500 mt-1">{(file.size / 1024 / 1024).toFixed(2)} MB · cliquer pour changer</p>
              </div>
            ) : (
              <div>
                <Mic className="h-8 w-8 mx-auto text-slate-400 mb-3" />
                <p className="text-sm text-slate-700 font-medium">Cliquer pour téléverser</p>
                <p className="text-xs text-slate-500 mt-1">MP3, WAV, M4A, WEBM · max 25MB</p>
                <p className="text-xs text-slate-400 mt-2">Transcription automatique via Whisper</p>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div>
          <Label className="text-xs uppercase tracking-widest mb-3 block">Mode d'analyse</Label>
          <RadioGroup value={verdictMode} onValueChange={setVerdictMode} className="space-y-2">
            <label className="flex items-start gap-3 cursor-pointer border border-slate-200 rounded-sm p-3 hover:border-slate-900 transition-colors" data-testid="mode-nuanced">
              <RadioGroupItem value="nuanced" id="m-nuanced" className="mt-1" />
              <div>
                <p className="text-sm font-medium">Analyse nuancée</p>
                <p className="text-xs text-slate-500">Faits, cadre légal, positions des parties, recommandations</p>
              </div>
            </label>
            <label className="flex items-start gap-3 cursor-pointer border border-slate-200 rounded-sm p-3 hover:border-slate-900 transition-colors" data-testid="mode-verdict">
              <RadioGroupItem value="verdict" id="m-verdict" className="mt-1" />
              <div>
                <p className="text-sm font-medium">Verdict tranché</p>
                <p className="text-xs text-slate-500">Direct : qui a raison, en quelques points</p>
              </div>
            </label>
          </RadioGroup>
        </div>

        <div>
          <Label className="text-xs uppercase tracking-widest mb-2 block">Titre (optionnel)</Label>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Ex: Litige garantie locative"
            className="rounded-sm mb-4"
            data-testid="analysis-title-input"
          />
          <Label className="text-xs uppercase tracking-widest mb-2 block">Contexte additionnel (optionnel)</Label>
          <Textarea
            value={context}
            onChange={(e) => setContext(e.target.value)}
            rows={3}
            placeholder="Précisions sur les parties, dates, etc."
            className="rounded-sm"
            data-testid="analysis-context-input"
          />
        </div>
      </div>

      <div className="flex items-center justify-between border-t border-slate-200 pt-6">
        <p className="text-xs text-slate-500 max-w-md">
          ⚠️ Avis indicatif uniquement — n'a pas valeur de jugement.
          Seul un tribunal peut trancher définitivement.
        </p>
        <Button
          type="submit"
          disabled={analyzing}
          className="rounded-sm bg-slate-900 hover:bg-slate-800 h-11 px-8"
          data-testid="submit-analysis-button"
        >
          {analyzing ? (
            <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Analyse en cours...</>
          ) : (
            <><Gavel className="h-4 w-4 mr-2" /> Analyser</>
          )}
        </Button>
      </div>
    </form>
  );
}

function AnalysisView({ analysis, onDelete }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm" data-testid="analysis-view">
      <div className="p-6 border-b border-slate-200 flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#B89C65] mb-1">
            {analysis.mode === "verdict" ? "Verdict tranché" : "Analyse nuancée"} · {analysis.source_type === "image" ? "Photo" : analysis.source_type === "audio" ? "Audio" : "Texte"}
          </p>
          <h2 className="font-serif-display text-2xl font-medium">{analysis.title}</h2>
          <p className="text-xs text-slate-500 mt-2">{new Date(analysis.created_at).toLocaleString("fr-BE")}</p>
        </div>
        <Button
          onClick={() => onDelete(analysis.analysis_id)}
          variant="outline"
          className="rounded-sm text-red-600 hover:text-red-700"
          data-testid="delete-viewed-analysis"
        >
          <Trash2 className="h-4 w-4 mr-2" /> Supprimer
        </Button>
      </div>

      {analysis.source_type === "image" && analysis.file_path && (
        <div className="p-6 border-b border-slate-200 bg-slate-50">
          <p className="text-xs uppercase tracking-widest text-slate-500 mb-3">Pièce analysée</p>
          <AuthedImage analysisId={analysis.analysis_id} />
        </div>
      )}

      {analysis.transcription && (
        <details className="p-6 border-b border-slate-200 bg-slate-50">
          <summary className="text-xs uppercase tracking-widest text-slate-500 cursor-pointer">Transcription audio</summary>
          <p className="text-sm text-slate-700 mt-3 whitespace-pre-wrap leading-relaxed" data-testid="audio-transcription">
            {analysis.transcription}
          </p>
        </details>
      )}

      <div className="p-8">
        <pre className="whitespace-pre-wrap text-sm leading-relaxed font-sans text-slate-800" data-testid="analysis-content">
          {analysis.content}
        </pre>
      </div>
    </div>
  );
}

function AuthedImage({ analysisId }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    (async () => {
      try {
        // Use httpOnly cookie via credentials:'include'. Fallback to legacy
        // localStorage token if any user still has one (graceful migration).
        const legacyToken = localStorage.getItem("auth_token");
        const headers = legacyToken ? { Authorization: `Bearer ${legacyToken}` } : {};
        const res = await fetch(`${API}/analyses/${analysisId}/file`, {
          credentials: "include",
          headers,
        });
        if (!res.ok) return;
        const blob = await res.blob();
        url = URL.createObjectURL(blob);
        setSrc(url);
      } catch (e) {
        console.warn("Image load error", e);
      }
    })();
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [analysisId]);
  if (!src) return <div className="h-32 flex items-center justify-center text-xs text-slate-400">Chargement...</div>;
  return <img src={src} alt="Pièce" className="max-h-96 rounded-sm border border-slate-200" data-testid="analyzed-image" />;
}
